import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import AudioButton from "@/components/AudioButton";
import ConfettiBurst from "@/components/ConfettiBurst";
import { Check, ArrowRight, RotateCcw, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ChildPlay() {
  const { id } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [c, setC] = useState(null);

  useEffect(() => { api.get(`/challenges/${id}`).then(r => setC(r.data)); }, [id]);

  if (!c) return <div className="min-h-screen bg-childhub"><TopBar variant="child"/><div className="p-12 text-center font-display text-xl">Loading...</div></div>;

  const submit = async (score, status = "completed") => {
    const { data } = await api.post("/progress", { challenge_id: c.id, score, status });
    await refresh();
    return data;
  };

  return (
    <div className="min-h-screen bg-childhub">
      <TopBar variant="child"/>
      <main className="max-w-4xl mx-auto p-4 sm:p-8">
        {c.type === "video" && <VideoLesson c={c} onComplete={()=>submit(100).then(()=>nav("/child"))}/>}
        {c.type === "quiz" && <Quiz c={c} submit={submit} nav={nav}/>}
        {c.type === "game" && <Game c={c} submit={submit} nav={nav}/>}
      </main>
    </div>
  );
}

function VideoLesson({ c, onComplete }) {
  const [done, setDone] = useState(false);
  return (
    <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="child-card p-6">
      <h1 className="font-display text-3xl font-bold text-slate-800 mb-2">{c.title}</h1>
      <p className="text-slate-600 mb-4">{c.caption_text}</p>
      <div className="aspect-video w-full rounded-2xl overflow-hidden border-4 border-sky-100 mb-4 bg-black">
        {/\.(mp4|webm|ogg)(\?|$)/i.test(c.video_url) ? (
          <video data-testid="lesson-video" src={c.video_url} controls className="w-full h-full"/>
        ) : (
          <iframe data-testid="lesson-video" src={c.video_url} title={c.title} className="w-full h-full" allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"/>
        )}
      </div>
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <AudioButton text={c.audio_overlay_text || c.caption_text} voice="child" label="Hear it" testId="video-audio"/>
        {!done ? (
          <button data-testid="video-continue" onClick={()=>{setDone(true); onComplete();}} className="btn-child-green">
            I watched it! <ArrowRight className="w-5 h-5 inline ml-1"/>
          </button>
        ) : <span className="text-emerald-600 font-bold">Great job!</span>}
      </div>
    </motion.div>
  );
}

function Quiz({ c, submit, nav }) {
  const questions = c.questions || [];
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [confetti, setConfetti] = useState(0);
  const [shake, setShake] = useState(false);
  const [feedback, setFeedback] = useState("");

  const q = questions[idx];

  const onPick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    const ok = i === q.answer;
    if (ok) { setCorrect(x=>x+1); setConfetti(x=>x+1); }
    else { setShake(true); setTimeout(()=>setShake(false), 400); }
  };

  const next = async () => {
    if (idx + 1 < questions.length) { setIdx(idx+1); setPicked(null); return; }
    // finish
    const score = Math.round((correctCount / questions.length) * 100);
    await submit(score);
    try {
      const { data } = await api.post("/ai/feedback", { challenge_title: c.title, score, attempts: 1, audience: "child" });
      setFeedback(data.text);
    } catch {}
    setDone(true);
  };

  if (done) {
    const score = Math.round((correctCount / questions.length) * 100);
    return (
      <motion.div initial={{opacity:0,scale:.8}} animate={{opacity:1,scale:1}} className="child-card p-8 text-center">
        <ConfettiBurst trigger={1}/>
        <Sparkles className="w-16 h-16 text-amber-400 mx-auto"/>
        <h2 className="font-display text-4xl font-bold text-slate-800 mt-2">You scored {score}!</h2>
        <p className="text-slate-600 text-lg mt-2 max-w-md mx-auto" data-testid="ai-feedback">{feedback || "Awesome effort!"}</p>
        <div className="mt-6 flex gap-3 justify-center">
          <button onClick={()=>nav("/child")} className="btn-child-green" data-testid="finish-home">Back home</button>
        </div>
      </motion.div>
    );
  }

  if (!q) return <div>Loading...</div>;

  return (
    <div className="child-card p-6 sm:p-8">
      <ConfettiBurst trigger={confetti}/>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold text-slate-500">Question {idx+1}/{questions.length}</span>
        <AudioButton text={q.q} voice="child" label="Hear" testId="quiz-audio"/>
      </div>
      <motion.h2 key={idx} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}
        className={`font-display text-2xl sm:text-3xl font-bold text-slate-800 mb-6 ${shake?"animate-shake":""}`}>
        {q.q}
      </motion.h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {q.options.map((opt, i) => {
          const isPicked = picked === i;
          const isCorrect = picked !== null && i === q.answer;
          const isWrongPick = isPicked && i !== q.answer;
          return (
            <button key={i} onClick={()=>onPick(i)} disabled={picked!==null}
              data-testid={`quiz-opt-${i}`}
              className={`p-5 rounded-3xl text-xl font-display font-bold border-4 transition shadow-[0_6px_0_#0284C7] active:translate-y-1
                ${isCorrect?"bg-emerald-400 text-white border-emerald-500":""}
                ${isWrongPick?"bg-rose-400 text-white border-rose-500":""}
                ${picked===null?"bg-white text-slate-800 border-sky-200 hover:bg-sky-50":""}`}>
              {opt} {isCorrect && <Check className="w-6 h-6 inline ml-1"/>}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div className="mt-6 text-right">
          <button onClick={next} className="btn-child-amber" data-testid="quiz-next">
            {idx+1 < questions.length ? "Next" : "Finish"} <ArrowRight className="w-5 h-5 inline ml-1"/>
          </button>
        </div>
      )}
    </div>
  );
}

function Game({ c, submit, nav }) {
  // Color Tap mini-game (10 rounds)
  const colors = [
    { name: "Red", cls: "bg-rose-500" },
    { name: "Blue", cls: "bg-sky-500" },
    { name: "Green", cls: "bg-emerald-500" },
    { name: "Yellow", cls: "bg-amber-400" },
    { name: "Purple", cls: "bg-violet-500" },
  ];
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [target, setTarget] = useState(() => colors[Math.floor(Math.random()*colors.length)]);
  const [done, setDone] = useState(false);
  const [confetti, setConfetti] = useState(0);

  const onTap = (col) => {
    if (col.name === target.name) { setScore(s=>s+10); setConfetti(x=>x+1); }
    if (round+1 >= 10) {
      const s = col.name === target.name ? score+10 : score;
      submit(s).then(()=>setDone(true));
      return;
    }
    setRound(r=>r+1);
    setTarget(colors[Math.floor(Math.random()*colors.length)]);
  };

  if (done) return (
    <div className="child-card p-8 text-center">
      <ConfettiBurst trigger={1}/>
      <h2 className="font-display text-4xl font-bold text-slate-800">You scored {score}!</h2>
      <button onClick={()=>nav("/child")} className="btn-child-green mt-4" data-testid="game-home">Back home</button>
    </div>
  );

  return (
    <div className="child-card p-6 sm:p-8 text-center">
      <ConfettiBurst trigger={confetti}/>
      <p className="text-slate-500 font-bold">Round {round+1}/10 · Score {score}</p>
      <div className="my-6">
        <p className="font-display text-2xl text-slate-700 mb-3">Tap the color:</p>
        <div className="font-display text-5xl font-bold animate-pop" data-testid="game-target">{target.name}</div>
      </div>
      <div className="grid grid-cols-5 gap-3 max-w-md mx-auto">
        {colors.map(col => (
          <button key={col.name} onClick={()=>onTap(col)} data-testid={`color-${col.name}`}
            className={`${col.cls} aspect-square rounded-2xl shadow-md hover:scale-105 active:scale-95 transition`}/>
        ))}
      </div>
      <div className="mt-4 flex justify-center">
        <AudioButton text={`Tap the color ${target.name}!`} voice="child" label="Hear" testId="game-audio"/>
      </div>
    </div>
  );
}
